// Mirrors sbly-dash-collector src/ecommerce/orderKitting (settings.ts + the
// order_kitting_log columns; the api Snowflake client lowercases column names).

export const ORDER_KITTING_NAMESPACE = 'order-kitting';

// Mirrors src/ecommerce/orderKitting/stores.js in the collector.
export const STORES = ['US', 'CA', 'AU', 'UK'] as const;
// admin.shopify.com/store/<handle> (the myshopify subdomain per storefront).
export const ADMIN_HANDLES: Record<string, string> = {
  US: 'dosaze',
  CA: 'dosazecanada',
  AU: 'dosaze-australia',
  UK: 'dosaze-uk',
};
export const CURRENCIES: Record<string, string> = {
  US: 'USD',
  CA: 'CAD',
  AU: 'AUD',
  UK: 'GBP',
};

// Log amounts are stored as plain numbers in the store's currency.
export function money(value: number | null | undefined, store: string): string {
  if (value == null) return '—';
  const currency = CURRENCIES[store];
  if (!currency) return Number(value).toFixed(2);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value));
}

export const MODES = { DRY_RUN: 'dry_run', LIVE: 'live' } as const;
export const OUTCOMES = {
  WOULD_KIT: 'would_kit',
  KITTED: 'kitted',
  SKIPPED: 'skipped',
  ERROR: 'error',
} as const;

export interface KittingComponent {
  sku: string;
  quantity: number;
}

export interface KittingRule {
  ruleId: string;
  store: string;
  enabled: boolean;
  // 1 (highest) … 5 (lowest). When two rules want the same units the
  // higher-priority rule consumes them first.
  priority: number;
  description: string;
  components: KittingComponent[];
  kitVariantId: number;
  kitSku: string;
}

export const PRIORITIES = [1, 2, 3, 4, 5];
export const PRIORITY_LABELS: Record<number, string> = {
  1: '1 — highest',
  2: '2',
  3: '3',
  4: '4',
  5: '5 — lowest',
};

// One entry of a log row's components_removed JSON. lineItemId is absent on
// rows logged before it was recorded — the UI falls back to SKU matching.
export interface RemovedComponent {
  lineItemId?: number;
  sku: string;
  fromQty: number;
  toQty: number;
}

// GET /order-kitting/order-detail — every line on one order, fetched live.
export interface OrderDetailLineItem {
  lineItemId: number;
  name: string | null;
  sku: string | null;
  quantity: number;
  currentQuantity: number;
  paidAmount: number;
}

export interface OrderDetail {
  id: number;
  name: string;
  createdAt: string;
  cancelledAt: string | null;
  tags: string[];
  financialStatus: string;
  fulfillmentStatus: string;
  currency: string;
  totalPrice: number;
  lineItemsTruncated: boolean;
  lineItems: OrderDetailLineItem[];
}

export interface OrderDetailResponse {
  store: string;
  currency: string;
  order: OrderDetail;
}

export function parseRemovedComponents(json: string | null): RemovedComponent[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as RemovedComponent[]) : [];
  } catch {
    return [];
  }
}

// GET /order-kitting/credentials — per-store Shopify token health.
export interface TokenStatus {
  configured: boolean;
  ok: boolean;
  error?: string;
  shopName?: string;
  domain?: string;
  scopes?: string[];
  missingScopes?: string[];
}

export interface StoreCredentials {
  store: string;
  currency: string;
  adminHandle: string;
  // That store's ShipBob → Shopify integration settings page (where the order
  // import delay lives). Per-store: it is keyed on the ShipBob channel id.
  shipbobSettingsUrl: string | null;
  read: TokenStatus;
  orderEdit: TokenStatus;
  canEvaluate: boolean;
  canKitLive: boolean;
}

export interface CredentialsResponse {
  requiredOrderEditScopes: string[];
  stores: StoreCredentials[];
}

// Where the 60-minute import delay is configured. ShipBob's own docs for the
// Shopify integration; the delay lives in the store's integration settings in
// the ShipBob dashboard.
export const SHIPBOB_DASHBOARD_URL = 'https://web.shipbob.com';
export const SHIPBOB_DELAY_DOC_URL =
  'https://support.shipbob.com/s/article/Shopify-Features-Capabilities';

// GET/POST /order-kitting/preview — what a rule would do to real orders.
export interface PreviewLineItem {
  sku: string;
  quantity: number;
  currentQuantity: number;
  paidAmount: number;
}

export interface PreviewOrder {
  orderId: number;
  orderName: string;
  createdAt: string;
  financialStatus: string;
  fulfillmentStatus: string;
  currency: string;
  totalPrice: number;
  lineItems: PreviewLineItem[];
  outcome: 'would_kit' | 'skipped' | 'no_match';
  times?: number;
  paidAmount?: number;
  componentsRemoved?: { sku: string; fromQty: number; toQty: number }[];
  reason?: string;
  blockers: string[];
}

export interface PreviewSample {
  limit: number;
  orders: PreviewOrder[];
  counts: Record<string, number>;
}

export interface PreviewResponse {
  store: string;
  currency: string;
  recent: PreviewSample;
  matching: PreviewSample;
}

export interface StoreSettings {
  // false = the scheduled job skips this store entirely (rules stay saved).
  enabled: boolean;
  // false = dry-run for this store; live is per store so rollout can go one
  // storefront at a time.
  live: boolean;
}

export interface OrderKittingSettings {
  minOrderAgeMinutes: number;
  maxOrderAgeMinutes: number;
  stores: Record<string, StoreSettings>;
  rules: KittingRule[];
}

export interface KittingAction {
  run_id: string;
  mode: string;
  app_env: string;
  store: string;
  order_id: number;
  order_name: string;
  order_created_at: string;
  rule_id: string | null;
  rule_description: string | null;
  times: number | null;
  components_removed: string | null; // JSON array of {sku, fromQty, toQty}
  kit_variant_id: number | null;
  kit_sku: string | null;
  paid_amount: number | null;
  kit_list_price: number | null;
  discount_applied: number | null;
  delta: number | null;
  outcome: string;
  reason: string | null;
  // Equal by design — the commit is gated on a zero delta. Shown side by side
  // so the "the customer pays exactly the same" guarantee is visible.
  order_total_before: number | null;
  order_total_after: number | null;
  created_at: string;
}

export interface KittingActionsResponse {
  rows: KittingAction[];
  total: number;
  limit: number;
  offset: number;
}

// One row of a store's variant catalog (GET /order-kitting/catalog) — feeds the
// SKU/kit dropdowns in the rule editor.
export interface CatalogVariant {
  sku: string;
  variantId: number;
  variantTitle: string;
  productId: number;
  productTitle: string;
  productStatus: string;
  price: string;
  inventoryQuantity: number;
  // true/false = stocked at the store's ShipBob location; null = the store has
  // no ShipBob location, so it cannot be determined and must not be treated as
  // a failure.
  shipbobStocked: boolean | null;
  shipbobQuantity: number | null;
}

export interface CatalogResponse {
  variants: CatalogVariant[];
  shipbobLocation: { id: string; name: string } | null;
}

export function variantAdminUrl(store: string, variant: CatalogVariant): string {
  const handle = ADMIN_HANDLES[store] ?? 'dosaze';
  return `https://admin.shopify.com/store/${handle}/products/${variant.productId}/variants/${variant.variantId}`;
}

// The stored shape for rules: a keyed object (never an array — Firebase RTDB
// drops empty arrays), written wholesale on every rules edit.
export function rulesToStored(rules: KittingRule[]): Record<string, unknown> {
  const stored: Record<string, unknown> = {};
  for (const rule of rules) {
    const { ruleId, ...rest } = rule;
    stored[ruleId] = rest;
  }
  return stored;
}
